import { useState, useEffect } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { countries } from 'data';
import { ApplicationState } from '../../../../../store';
import { saveVisaPrepForm } from '../../../../../store/student/actions';
import { createUpdateVPIFieldAction } from '../../../../../store/student/visaPrepForm/actions';
import QuestionPage from '../../../../templates/question';
import { VQ } from '../../../../../store/student/types';
import {
  DateInputWithConfirm,
  InputWithConfirm,
  AutocompleteInputWithConfirm,
  RadioOptionsWithConfirm,
  IQuestion,
  getStringFromDate,
  getDateFromString,
  isValidDate,
} from 'shared-components';

import { isPersonTravellingWith } from '../../../../../utilities/pages';
import { Pages } from '../../../../../store/student/visaPrepForm/types';
import { getPageStatus } from './utils';
import { IDate } from '../../../../../types';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from "react-i18next";

const VisaPrepFormFamilyPage4 = (_props: any) => {
    const { t } = useTranslation('common');
  // dispatch actions
  const dispatch = useDispatch();

  const onNext = () => {
    dispatch(saveVisaPrepForm(Pages.PAGE3_4));
  };

  const onChange = (fieldName: string, value: any, refValue?: any) =>
    dispatch(createUpdateVPIFieldAction(fieldName, value, refValue));
  const navigate = useNavigate();
  const onBackToTaskList = () => navigate(-1);

  // state props from store
  const studentVisaPrepForm = useSelector(({ student }: ApplicationState) => student.visa, shallowEqual);

  const currentStep = 4;
  const steps = 5;
  const nationalitiesNames = countries.map(nationality => nationality.nationality);

  const {
    hasGuardiansPersonalDetails,
    guardianNationality,
    guardianSameNationality,
    guardianFamilyName,
    guardianGivenName,
    guardianDob,
    guardianPlaceOfBirth,
    guardianTravellingWith,
    guardianUkVisa,
    guardianHasPassport,
    guardianPassportNumber,
  } = studentVisaPrepForm;

  // component state
  const [complete, setComplete] = useState(false);
  const [dob, setDob] = useState<IDate>(() => getDateFromString(guardianDob));
  const [dobError, setDobError] = useState('');
  const [nationalitySuggestions, setNationalitySuggestions] = useState(nationalitiesNames);

  useEffect(() => {
    const { complete: newComplete } = getPageStatus({
      hasGuardiansPersonalDetails,
      guardianNationality,
      guardianSameNationality,
      guardianFamilyName,
      guardianGivenName,
      guardianDob,
      guardianPlaceOfBirth,
      guardianTravellingWith,
      guardianUkVisa,
      guardianHasPassport,
      guardianPassportNumber,
      dobError,
    });
    if (newComplete !== complete) {
      setComplete(newComplete);
    }
  }, [
    complete,
    hasGuardiansPersonalDetails,
    guardianNationality,
    guardianSameNationality,
    guardianFamilyName,
    guardianGivenName,
    guardianDob,
    guardianPlaceOfBirth,
    guardianTravellingWith,
    guardianUkVisa,
    guardianHasPassport,
    guardianPassportNumber,
    dobError,
  ]);

  const onYesNoQuestionButtonChange = (key: VQ, question: IQuestion) => {
    onChange(key, question.label === 'Yes');
  };

  const onNationalityChange = (evt: any) => {
    const value = evt.currentTarget.value || '';
    const suggestions = nationalitiesNames.filter(s => s.toLowerCase().includes(value.toLowerCase()));
    setNationalitySuggestions(suggestions);
    onChange(VQ.GUARDIAN_NATIONALITY, value);
  };

  const onBlurNationality = (evt: any) => {
    const { value } = evt.currentTarget;
    if (nationalitiesNames.includes(value)) {
      onChange(VQ.GUARDIAN_NATIONALITY, value);
    } else {
      onChange(VQ.GUARDIAN_NATIONALITY, '');
    }
    setNationalitySuggestions(nationalitiesNames);
  };

  const validateDate = (dateValue: IDate) => {
    const { YYYY, DD, MM } = dateValue;
    if (YYYY && DD && MM) {
      const isValid = isValidDate(dateValue);
      if (!isValid) {
        setDobError(`Doesn't look like a valid date`);
      } else {
        onChange(VQ.GUARDIAN_DOB, getStringFromDate(dateValue));
        setDobError('');
      }
    }
  };

  return (
    <QuestionPage
      complete={complete}
      currentStep={currentStep}
      steps={steps}
      onNext={onNext}
      onBackToTaskList={onBackToTaskList}
      section={'Family'}
    >
      <RadioOptionsWithConfirm
        key={VQ.GUARDIANS_PERSONAL_DETAILS}
        id={VQ.GUARDIANS_PERSONAL_DETAILS}
        label={t('common:doYouHaveAGuardianThatYouHaveNot')}
        onClick={(question: IQuestion) => onYesNoQuestionButtonChange(VQ.GUARDIANS_PERSONAL_DETAILS, question)}
        isInline={true}
        questions={[
          {
            char: 'Y',
            label: 'Yes',
            selected: hasGuardiansPersonalDetails === true,
          },
          {
            char: 'N',
            label: 'No',
            selected: hasGuardiansPersonalDetails === false,
          },
        ]}
      />

      {hasGuardiansPersonalDetails === true ? (
        <>
          <AutocompleteInputWithConfirm
            key={VQ.NATIONALITY}
            id={VQ.NATIONALITY}
            value={guardianNationality}
            label={t('common:guardianSNationality')}
            onChange={onNationalityChange}
            suggestions={nationalitySuggestions}
            onBlur={onBlurNationality}
          />

          <RadioOptionsWithConfirm
            key={VQ.SAME_NATIONALITY}
            id={VQ.SAME_NATIONALITY}
            label={t('common:haveTheyAlwaysHadTheSameNational')}
            onClick={(question: IQuestion) => onChange(VQ.GUARDIAN_SAME_NATIONALITY, question.label === 'Yes')}
            isInline={true}
            questions={[
              {
                char: 'Y',
                label: 'Yes',
                selected: guardianSameNationality === true,
              },
              {
                char: 'N',
                label: 'No',
                selected: guardianSameNationality === false,
              },
            ]}
          />

          <InputWithConfirm
            key={VQ.FAMILY_NAME}
            id={VQ.FAMILY_NAME}
            value={guardianFamilyName}
            label={t('common:guardianSFamilyName')}
            onChange={(evt: any) => onChange(VQ.GUARDIAN_FAMILY_NAME, evt.currentTarget.value)}
          />

          <InputWithConfirm
            key={VQ.GIVEN_NAME}
            id={VQ.GIVEN_NAME}
            value={guardianGivenName}
            label={t('common:guardianSGivenName')}
            onChange={(evt: any) => onChange(VQ.GUARDIAN_GIVEN_NAME, evt.currentTarget.value)}
          />

          <DateInputWithConfirm
            key={VQ.DOB}
            id={VQ.DOB}
            label={t('common:guardianSDateOfBirth')}
            value={dob}
            errorText={dobError}
            onChangeDD={(evt: any) => {
              const spouseDob = {
                ...dob,
                DD: evt.currentTarget.value,
              };
              setDob(spouseDob);
            }}
            onChangeMM={(evt: any) => {
              const spouseDob = {
                ...dob,
                MM: evt.currentTarget.value,
              };
              setDob(spouseDob);
            }}
            onChangeYY={(evt: any) => {
              const { value } = evt.currentTarget;
              if (value.length === 4) {
                const spouseDob = {
                  ...dob,
                  YYYY: value,
                };
                setDob(spouseDob);

                validateDate({
                  ...dob,
                  YYYY: value,
                });
              } else {
                const spouseDob = {
                  ...dob,
                  YYYY: value,
                };
                setDob(spouseDob);
              }
            }}
            onBlur={(_evt: any) =>
              validateDate({
                ...dob,
              })
            }
          />

          <InputWithConfirm
            key={VQ.PLACE_OF_BIRTH}
            id={VQ.PLACE_OF_BIRTH}
            value={guardianPlaceOfBirth}
            label={t('common:guardianSPlaceOfBirth')}
            onChange={(evt: any) => onChange(VQ.GUARDIAN_PLACE_OF_BIRTH, evt.currentTarget.value)}
          />

          <RadioOptionsWithConfirm
            key={VQ.TRAVELLING_WITH}
            id={VQ.TRAVELLING_WITH}
            label={t('common:isThisPersonTravellingWithYou')}
            onClick={(question: IQuestion) => onChange(VQ.GUARDIAN_TRAVELLING_WITH, question.label)}
            questions={[
              {
                char: '1',
                label: 'Yes - under 1 month',
                selected: guardianTravellingWith === 'Yes - under 1 month',
              },
              {
                char: '2',
                label: 'Yes - over 1 month',
                selected: guardianTravellingWith === 'Yes - over 1 month',
              },
              {
                char: '3',
                label: 'No - not travelling',
                selected: guardianTravellingWith === 'No - not travelling',
              },
            ]}
          />
          {isPersonTravellingWith(guardianTravellingWith) ? (
            <>
              <RadioOptionsWithConfirm
                key={VQ.HAS_UK_VISA}
                id={VQ.HAS_UK_VISA}
                label={t('common:doTheyHaveAVisaForTheUk')}
                onClick={(question: IQuestion) => onChange(VQ.GUARDIAN_UK_VISA, question.label === 'Yes')}
                isInline={true}
                questions={[
                  {
                    char: 'Y',
                    label: 'Yes',
                    selected: guardianUkVisa === true,
                  },
                  {
                    char: 'N',
                    label: 'No',
                    selected: guardianUkVisa === false,
                  },
                ]}
              />

              <RadioOptionsWithConfirm
                key={VQ.HAS_PASSPORT}
                id={VQ.HAS_PASSPORT}
                label={t('common:doTheyHaveAPassportOrTravelDocum')}
                onClick={(question: IQuestion) => onChange(VQ.GUARDIAN_HAS_PASSPORT, question.label === 'Yes')}
                isInline={true}
                questions={[
                  {
                    char: 'Y',
                    label: 'Yes',
                    selected: guardianHasPassport === true,
                  },
                  {
                    char: 'N',
                    label: 'No',
                    selected: guardianHasPassport === false,
                  },
                ]}
              />
              {guardianHasPassport ? (
                <InputWithConfirm
                  key={VQ.PASSPORT_NUMBER}
                  id={VQ.PASSPORT_NUMBER}
                  value={guardianPassportNumber}
                  label={t('common:currentPassportOrTravelDocumentN')}
                  onChange={(evt: any) => onChange(VQ.GUARDIAN_PASSPORT_NUMBER, evt.currentTarget.value)}
                />
              ) : null}
            </>
          ) : null}
        </>
      ) : null}
    </QuestionPage>
  );
};
export default VisaPrepFormFamilyPage4;
